import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { OnCustomizeConciergeProductClickHandler, ConciergeProduct } from './conciergeProduct.types';
import type { BoxProps } from '@withjoy/joykit';
import { Box, ButtonV2, DialogV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { LottiePlayer } from '@shared/components/LottiePlayer';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useVimeoPlayerContext } from '@apps/registry/common/components/Catalog/utils/vimeoPlayer';
import { VimeoVideo, useOptimizedVimeoReference } from '@shared/components/VimeoVideo';
import { useFeatureValue } from '@shared/core/featureFlags';
import type { ConciergeServiceCategory } from './conciergeConfig';
import { getConciergeSuiteUnitPrice } from './conciergeConfig';
import { useFormatPriceString } from '@apps/card/hooks/useFormattedPriceString';

interface SectionProps extends BoxProps {
  title: string;
  children: React.ReactNode;
}
const Section = ({ title, children, ...restProps }: SectionProps) => {
  return (
    <Flex
      flexDirection="column"
      rowGap={6}
      {...restProps}
    >
      <TextV2 typographyVariant="hed4">{title}</TextV2>
      {children}
    </Flex>
  );
};

/**
 * The offering's looping overview video over its thumbnail. With `vimeoPlaybackOptimizationEnabled`
 * on, the shared `VimeoVideo` plays it only while visible and the user is active; only the thumbnail
 * shows while the flag loads.
 */
const Video = ({ url: urlProp, thumbnailUrl }: { url: string; thumbnailUrl: string }) => {
  const { tq } = useTranslation('stationery');
  const { video: optimizedVideo, isFlagLoading } = useOptimizedVimeoReference(urlProp);
  const isLegacyEmbed = !optimizedVideo && !isFlagLoading;
  const { VimeoPlayer, prepareForUse } = useVimeoPlayerContext();
  const playerRef = useRef<HTMLIFrameElement>(null);
  const videoUrl = useMemo(() => {
    const url = new URL(urlProp);
    const searchParams = url.searchParams;
    searchParams.append('muted', '1');
    searchParams.append('loop', '1');
    searchParams.append('background', '1');
    return url.href;
  }, [urlProp]);

  useEffect(() => {
    prepareForUse();
  }, [prepareForUse]);

  useEffect(() => {
    // Opting to manually play the video instead of leveraging `autoplay=1` to resolve chrome issue on windows machine
    // `isLegacyEmbed` re-runs this once the flag resolves off and the iframe mounts.
    if (playerRef.current && VimeoPlayer) {
      const player = new VimeoPlayer(playerRef.current);
      player.play().catch(() => {
        //
      });
    }
  }, [VimeoPlayer, isLegacyEmbed]);

  return (
    <Box
      position="relative"
      paddingTop="177.78%"
      backgroundColor="mono1"
      backgroundPosition="center"
      backgroundSize="cover"
      backgroundRepeat="no-repeat"
      backgroundImage={`url('${thumbnailUrl}')`}
    >
      {optimizedVideo && (
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
        >
          <VimeoVideo
            videoId={optimizedVideo.videoId}
            privacyHash={optimizedVideo.privacyHash}
            title={tq`Product Overview Video`}
          />
        </Box>
      )}
      {isLegacyEmbed && (
        <Box
          ref={playerRef}
          as="iframe"
          border="none"
          outline="none"
          width="100%"
          height="100%"
          position="absolute"
          top={0}
          left={0}
          title={tq`Product Overview Video`}
          src={videoUrl}
        />
      )}
    </Box>
  );
};

const OverviewScreen = ({
  data,
  handleOnNotifyMeClick,
  conciergeProductSortIndex,
  orderedProducts,
  category,
  onCustomizeConciergeProductClick,
}: {
  data: ConciergeProduct;
  handleOnNotifyMeClick: () => void;
  conciergeProductSortIndex: number;
  orderedProducts: ReadonlyArray<ConciergeProduct>;
  category: ConciergeServiceCategory;
  onCustomizeConciergeProductClick: OnCustomizeConciergeProductClickHandler;
}) => {
  const { buttonInteracted } = useCardTelemetry();
  const { value: printDesignConciergeCheckoutEnabledValue } = useFeatureValue('printDesignConciergeCheckoutEnabled');
  const isPrintDesignConciergeCheckoutEnabled = printDesignConciergeCheckoutEnabledValue === 'on';
  const formatPrice = useFormatPriceString();

  const { t } = useTranslation('stationery');

  const trans = t('dashboard', 'conciergeServiceSection', 'dialog', 'overviewScreen');
  const sectionTrans = trans.sections;

  const { longName, descriptionHighlights, videoUrl: videoUrlProp, imageUrl, suiteHighlights, videoThumbnailUrl, printingBusinessDaysQuantity } = data;
  const costPerHundred = formatPrice(getConciergeSuiteUnitPrice(category, data.identifier, 100));

  const stepList = useMemo(() => {
    return sectionTrans.howItWorks.steps.map(step => ({
      title: step.title({ quantity: printingBusinessDaysQuantity }),
      description: step.description(),
    }));
  }, [sectionTrans, printingBusinessDaysQuantity]);

  return (
    <>
      <DialogV2.Body>
        <Flex
          flexDirection={{ _: 'column', sm2: 'row' }}
          rowGap={7}
          columnGap={8}
        >
          {/* Left Panel */}
          <Flex
            flexDirection="column"
            width={{ _: '100%', sm2: '40%' }}
            flexShrink={0}
            rowGap={6}
          >
            {videoThumbnailUrl && videoUrlProp && videoThumbnailUrl.trim() !== '' && videoUrlProp.trim() !== '' ? (
              <Video
                thumbnailUrl={videoThumbnailUrl}
                url={videoUrlProp}
              />
            ) : null}
            <Box
              backgroundImage={`url('${imageUrl}')`}
              backgroundPosition="center"
              backgroundSize={'cover'}
              width="100%"
              paddingTop="100%"
            />
          </Flex>

          {/* Right Panel */}
          <Flex
            flexDirection="column"
            paddingRight={{ sm2: 8 }}
          >
            <Flex
              flexDirection="column"
              rowGap={3}
            >
              {!printDesignConciergeCheckoutEnabledValue && <TextV2 typographyVariant="label3">{trans.comingSoonLabel()}</TextV2>}
              <TextV2
                maxWidth={320}
                typographyVariant="display1"
                fontWeight="semibold"
              >
                {longName}
              </TextV2>
              <TextV2 typographyVariant="label2">{trans.costLabel[category]({ cost: costPerHundred })}</TextV2>
            </Flex>

            <Box
              marginTop={5}
              as="ul"
              typographyVariant="body1"
              listStyle="disc"
              paddingLeft={5}
              style={{ listStylePosition: 'outside' }}
            >
              {descriptionHighlights.map((item, idx) => {
                return <li key={idx}>{item.text}</li>;
              })}
            </Box>
            {suiteHighlights && (
              <Section
                title={sectionTrans.suiteHighlights.title[category]()}
                marginTop={6}
              >
                <Box
                  as="ul"
                  typographyVariant="body1"
                  listStyle="disc"
                  paddingLeft={5}
                  style={{ listStylePosition: 'outside' }}
                >
                  {suiteHighlights.map((item, idx) => (
                    <li key={idx}>{item.text}</li>
                  ))}
                </Box>
              </Section>
            )}

            <Section
              title={sectionTrans.overview.title()}
              marginTop={6}
            >
              <TextV2 typographyVariant="body1">{sectionTrans.overview.description()}</TextV2>
            </Section>

            <Section
              marginTop={8}
              title={sectionTrans.howItWorks.title()}
            >
              <Flex
                flexDirection="column"
                rowGap={6}
              >
                {stepList.map((item, idx) => (
                  <Flex
                    flexDirection="column"
                    key={item.title}
                    rowGap={2}
                  >
                    <Flex
                      columnGap={3}
                      alignItems="center"
                    >
                      <Flex
                        size={28}
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="full"
                        backgroundColor="mono2"
                      >
                        {idx + 1}
                      </Flex>
                      <TextV2 typographyVariant="button1">{item.title}</TextV2>
                    </Flex>
                    <Box paddingLeft={36}>
                      <TextV2 typographyVariant={'body1'}>{item.description}</TextV2>
                    </Box>
                  </Flex>
                ))}
              </Flex>
            </Section>
          </Flex>
        </Flex>
      </DialogV2.Body>
      <DialogV2.Footer
        justifyContent="center"
        borderTop="1px solid"
        borderTopColor="mono3"
      >
        <ButtonV2
          intent="neutral"
          shape="rounded"
          width="332px"
          onClick={() => {
            buttonInteracted({
              name: isPrintDesignConciergeCheckoutEnabled ? 'paperlustConciergeService_dialog_orderNowCta' : 'paperlustConciergeService_dialog_notifyMeCta',
              suite: longName,
              cost: data.costPerHundredString,
              sortIndex: conciergeProductSortIndex,
              orderOfItems: orderedProducts.map(product => product.longName),
            });
            if (isPrintDesignConciergeCheckoutEnabled) {
              onCustomizeConciergeProductClick(data);
            } else {
              handleOnNotifyMeClick();
            }
          }}
        >
          {isPrintDesignConciergeCheckoutEnabled ? trans.orderNowCta() : trans.notifyMeCta()}
        </ButtonV2>
      </DialogV2.Footer>
    </>
  );
};

interface SuccessScreenProps {
  handleOnClose: () => void;
}
const SuccessScreen = ({ handleOnClose }: SuccessScreenProps) => {
  const { t2 } = useTranslation('stationery');

  const trans = t2('dashboard', 'conciergeServiceSection', 'dialog', 'successScreen');
  return (
    <DialogV2.Body>
      <Flex
        flexDirection="column"
        rowGap={7}
        alignItems="center"
        textAlign="center"
        width={{ sm2: 360 }}
        marginX="auto"
      >
        <Box width={240}>
          <LottiePlayer
            autoplay={true}
            lottieUrl="https://withjoy.com/assets/public/messaging/partnerships/paperlust/lottie/notify-success.json"
          />
        </Box>

        <Box>
          <TextV2
            typographyVariant="display1"
            fontWeight={600}
            textAlign="center"
          >
            {trans.title}
          </TextV2>
          <TextV2
            marginTop={3}
            typographyVariant="body1"
          >
            {trans.description}
          </TextV2>
        </Box>

        <ButtonV2
          intent="neutral"
          fullWidth
          onClick={handleOnClose}
        >
          {trans.allDoneCta}
        </ButtonV2>
      </Flex>
    </DialogV2.Body>
  );
};
interface RootProps {
  category: ConciergeServiceCategory;
  selectedConciergeProduct: ConciergeProduct;
  conciergeProductSortIndex: number;
  orderedProducts: ReadonlyArray<ConciergeProduct>;
  step: 1 | 2;
  setStep: (step: 1 | 2) => void;
  onClose: () => void;
  onCustomizeConciergeProductClick: OnCustomizeConciergeProductClickHandler;
}
const Root = (props: RootProps) => {
  const { category, onClose, selectedConciergeProduct, conciergeProductSortIndex, orderedProducts, onCustomizeConciergeProductClick, step, setStep } = props;

  const handleOnNotifyMeClick = useCallback(() => {
    setStep(2);
  }, [setStep]);

  return step === 1 ? (
    <OverviewScreen
      onCustomizeConciergeProductClick={onCustomizeConciergeProductClick}
      category={category}
      conciergeProductSortIndex={conciergeProductSortIndex}
      orderedProducts={orderedProducts}
      data={selectedConciergeProduct}
      handleOnNotifyMeClick={handleOnNotifyMeClick}
    />
  ) : (
    <SuccessScreen handleOnClose={onClose} />
  );
};

interface ConciergeProductDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedConciergeProduct: ConciergeProduct | undefined;
  conciergeProductSortIndex: number;
  /** The category's products in the order the gallery shows them, which telemetry reports. */
  orderedProducts: ReadonlyArray<ConciergeProduct>;
  category: ConciergeServiceCategory;
  onCustomizeConciergeProductClick: OnCustomizeConciergeProductClickHandler;
}

export const ConciergeProductDialog = (props: ConciergeProductDialogProps) => {
  const { isOpen, onClose, selectedConciergeProduct, conciergeProductSortIndex, orderedProducts, onCustomizeConciergeProductClick, category } = props;

  // Managing step state here so that we can drive the dialog size based on the step
  const [step, setStep] = useState<1 | 2>(1);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        // Wrapping this in a set timeout to prevent the state reset from happening before the animation finishes
        setStep(1);
      }, 500);
    }
  }, [isOpen]);

  return (
    <DialogV2
      overrides={{
        Body: {
          props: {
            paddingTop: [60, 60, 8],
          },
        },
        CloseButton: {
          props: {
            top: [12, null, 6],
          },
        },
      }}
      stickyBehavior="footer"
      isOpen={isOpen}
      onClose={onClose}
      size={step === 1 ? '3xl' : 'xl'}
    >
      {selectedConciergeProduct && (
        <>
          <DialogV2.CloseButton />
          <Root
            onCustomizeConciergeProductClick={onCustomizeConciergeProductClick}
            category={category}
            conciergeProductSortIndex={conciergeProductSortIndex}
            orderedProducts={orderedProducts}
            onClose={onClose}
            setStep={setStep}
            step={step}
            selectedConciergeProduct={selectedConciergeProduct}
          />
        </>
      )}
    </DialogV2>
  );
};
